// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { PowerBIEmbed } from 'powerbi-client-react';
import { models, Embed, service } from 'powerbi-client';
import React, { useContext, useEffect, useState } from 'react';

import qs from 'qs';
import { useParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import { Stack } from '@chakra-ui/layout';
import api from '~/utils/api';
import './Power.css';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';

const clearObj = {
  reportConfig: {
    id: undefined,
    name: undefined,
    embedUrl: undefined,
    filters: undefined,
  },
  accessToken: undefined,
};

export const Report: React.VFC = () => {
  const { groups, headOffices, branches } = useContext(FilterContext);
  const [reportParams, setReportParams] = useState(clearObj);
  const { id } = useParams<{ id: string }>();
  const [possuiErro, setPossuiErro] = useState<boolean>(false);
  const [erroRelatorio, setErroRelatorio] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [eventHandlersMap, setEventHandlersMap] = useState<
    Map<
      string,
      (event?: service.ICustomEvent<any>, embeddedEntity?: Embed) => void | null
    >
  >(
    new Map([
      ['loaded', () => console.log('Report has loaded')],
      ['rendered', () => console.log('Report has rendered')],
      [
        'error',
        (event?: service.ICustomEvent<any>) => {
          if (event) {
            console.error(event.detail);
          }
        },
      ],
      ['visualClicked', () => console.log('visual clicked')],
      ['pageChanged', event => console.log(event)],
    ]),
  );

  useEffect(() => {
    setReportParams(clearObj);
    setIsLoading(true);
    api
      .get('power', {
        params: {
          dashboardId: id,
          groups: groups.map(group => group.value),
          headoffices: headOffices.map(headOffice => headOffice.value),
          branches: branches.map(branch => branch.value),
        },
        paramsSerializer: params => {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      })
      .then(res => {
        setPossuiErro(false);
        setErroRelatorio('');
        setIsLoading(false);
        const { reportConfig, accessToken, filters } = res.data;
        reportConfig.filters = filters;
        setReportParams({ reportConfig, accessToken });
      })
      .catch(error => {
        setPossuiErro(true);
        setIsLoading(false);
        setErroRelatorio(
          error && error.response
            ? error.response.data.message
            : 'Erro ao carregar relatório! Verifique os dados do dashboard cadastrado.',
        );
      });
  }, [id]);
  return (
    <Stack px="8" py="4">
      {isLoading ? (
        <Flex justify="center">
          <Spinner />
        </Flex>
      ) : (
        <>
          <div className="container">
            {!possuiErro && (
              <Alert
                bg="#f7f2fa"
                color="#9246ce"
                fontSize="sm"
                py={1}
                px={2}
                my={2}
              >
                <AlertTitle>Atenção:</AlertTitle>
                <AlertDescription>
                  Para filtrar os dados no dashboard, utilize o filtro interno
                  disponível diretamente no próprio dashboard.
                </AlertDescription>
              </Alert>
            )}
            <div className="controls">
              {!possuiErro && reportParams.accessToken && (
                <PowerBIEmbed
                  cssClassName="report-container"
                  embedConfig={{
                    type: 'report',
                    id: reportParams.reportConfig.id,
                    embedUrl: reportParams.reportConfig.embedUrl,
                    accessToken: reportParams.accessToken,
                    tokenType: models.TokenType.Embed,
                    filters: reportParams.reportConfig.filters,
                    settings: {
                      panes: {
                        filters: {
                          expanded: false,
                          visible: false,
                        },
                      },
                      background: models.BackgroundType.Transparent,
                    },
                  }}
                />
              )}
              {possuiErro && <span>{erroRelatorio}</span>}
            </div>
          </div>
        </>
      )}
    </Stack>
  );
};
